import React, { useEffect, useRef, useState } from "react";
import { MapPin, LocateFixed, Building2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Type-ahead over REAL listing localities/cities (GET /api/locations/suggest) + GPS "Use my location".
// onPick({type:'city'|'locality', city, locality, label}) | onNear({lat,lng}) | onText(q) for free text.
export default function LocationSearch({ value = "", onChange, onPick, onNear, placeholder = "Area, locality, city or pincode", compact = false, showGps = true, testId = "location-search", autoFocus = false }) {
  const [q, setQ] = useState(value);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [active, setActive] = useState(-1);
  const box = useRef(null);

  useEffect(() => { setQ(value); }, [value]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setBusy(true);
      api.get("/locations/suggest", { params: { q, limit: 8 } }).then(({ data }) => setItems(data.items)).catch(() => setItems([])).finally(() => setBusy(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (it) => {
    setQ(it.label); setOpen(false); setActive(-1);
    onChange?.(it.label);
    onPick?.(it);
  };

  const gps = () => {
    if (!navigator.geolocation) { toast.error("Location is not supported on this device/browser"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGpsBusy(false); setOpen(false); onNear?.({ lat: +pos.coords.latitude.toFixed(5), lng: +pos.coords.longitude.toFixed(5) }); },
      (err) => { setGpsBusy(false); toast.error(err.code === 1 ? "Location permission denied — allow it in your browser settings" : "Could not get your location"); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0 && items[active]) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={box} className="relative w-full" data-testid={testId}>
      <div className={`flex items-center gap-2 ${compact ? "h-10 rounded-md border border-[#1A1F2C]/15 bg-[#FDFBF7] px-3" : "px-3"}`}>
        <MapPin size={16} className="text-[#C85A32] shrink-0" />
        <input
          value={q}
          autoFocus={autoFocus}
          onChange={(e) => { setQ(e.target.value); onChange?.(e.target.value); setOpen(true); setActive(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="flex-1 min-w-0 bg-transparent outline-none text-sm h-10 placeholder:text-[#5C6B7A]"
          autoComplete="off"
          data-testid={`${testId}-input`}
        />
        {q && <button type="button" onClick={() => { setQ(""); onChange?.(""); setItems([]); }} className="text-[#5C6B7A] hover:text-[#1A1F2C]" aria-label="clear" data-testid={`${testId}-clear`}><X size={14} /></button>}
        {showGps && (
          <button type="button" onClick={gps} disabled={gpsBusy} title="Use my current location" className="shrink-0 inline-flex items-center gap-1 text-xs text-[#2B3A67] hover:text-[#C85A32] font-medium" data-testid={`${testId}-gps`}>
            {gpsBusy ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />} <span className="hidden sm:inline">Near me</span>
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-lg border border-[#1A1F2C]/10 bg-[#FDFBF7] shadow-2xl overflow-hidden text-left" data-testid={`${testId}-dropdown`}>
          {busy && items.length === 0 ? (
            <div className="p-3 text-xs text-[#5C6B7A]">Searching real listings…</div>
          ) : items.length === 0 ? (
            <div className="p-3 text-xs text-[#5C6B7A]" data-testid={`${testId}-empty`}>No listings in “{q}” yet — try a nearby city, or tap “Near me”.</div>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {items.map((it, i) => (
                <li key={`${it.type}-${it.label}`}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(it)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-[#F0EBE1] ${active === i ? "bg-[#F0EBE1]" : ""}`}
                    data-testid={`${testId}-item-${i}`}
                  >
                    <span className={`grid place-items-center w-7 h-7 rounded-full ${it.type === "city" ? "bg-[#2B3A67]/10 text-[#2B3A67]" : "bg-[#C85A32]/10 text-[#C85A32]"}`}>
                      {it.type === "city" ? <Building2 size={13} /> : <MapPin size={13} />}
                    </span>
                    <span className="flex-1 min-w-0 truncate text-[#1A1F2C]">{it.label}</span>
                    <span className="text-xs text-[#5C6B7A] tabular-nums shrink-0">{it.count} listing{it.count === 1 ? "" : "s"}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {showGps && (
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={gps} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-[#2B3A67] border-t border-[#1A1F2C]/10 hover:bg-[#F0EBE1]" data-testid={`${testId}-gps-row`}>
              <LocateFixed size={14} /> Use my current location — show rentals near me
            </button>
          )}
        </div>
      )}
    </div>
  );
}
